import { CSmartTable } from '@coreui/react-pro'
import FilterAlt from '@mui/icons-material/FilterAlt'
import { Button, Chip, Dialog, Pagination } from '@mui/material'
import DateRangePicker from '@wojtekmaj/react-daterange-picker'
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

// Helper function to get user's timezone (replaces Intl.DateTimeFormat().resolvedOptions().timeZone)
function guessTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    // Validate timezone string
    if (tz && typeof tz === 'string' && tz.length > 0) {
      return tz
    }
    return 'UTC'
  } catch {
    return 'UTC'
  }
}

// Helper function to safely create a dayjs date in a timezone
function safeDayjsTz(timezone) {
  try {
    const tz = timezone || guessTimezone()
    // Validate timezone string before using it
    if (!tz || typeof tz !== 'string' || tz.trim().length === 0) {
      return dayjs()
    }
    
    // Create date in local time first, then convert to timezone
    const localDate = dayjs()
    if (!localDate.isValid()) {
      return dayjs()
    }
    
    // Try to convert to timezone
    const tzDate = localDate.tz(tz)
    
    // Validate the result
    if (tzDate.isValid()) {
      // Double-check by converting to Date and checking if it's finite
      try {
        const dateObj = tzDate.toDate()
        if (isFinite(dateObj.getTime())) {
          return tzDate
        }
      } catch {
        // If toDate() fails, fall back to local
        return dayjs()
      }
    }
  } catch (error) {
    // If timezone conversion fails, fall back to local time
    console.warn('Timezone conversion failed, using local time:', error)
  }
  return dayjs()
}

// Helper function to safely convert dayjs to Date, ensuring the date is valid and finite
function safeToDate(dayjsDate) {
  if (!dayjsDate || !dayjs.isDayjs(dayjsDate) || !dayjsDate.isValid()) {
    // Return a valid default date if the input is invalid
    return safeDayjsTz().startOf('day').toDate()
  }
  const date = dayjsDate.toDate()
  // Check if the date is valid and finite
  if (!isFinite(date.getTime())) {
    return safeDayjsTz().startOf('day').toDate()
  }
  return date
}
import { useEffect, useState } from 'react'
import 'react-calendar/dist/Calendar.css'
import {
  EDVCDateContainer,
  EDVCParent,
  FloatingHeaderContainerDayCal,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import BookedEventLogsDrawer from 'src/clinic/components/ClinicManageBookingsPage/components/BookedEventLogsDrawer'
import { getCPDataWithParams } from 'src/clinic/services'
import ClinicBrandPayoutReportsViewDetails from 'src/clinic/views/clinicBrandPayoutReports/components/ClinicBrandPayoutReportsViewDetails'
import PayoutDistributionReportFeeDetails from 'src/clinic/views/clinicBrandPayoutReports/components/PayoutDistributionReportFeeDetails'
import PayoutDistributionReportModelDetails from 'src/clinic/views/clinicBrandPayoutReports/components/PayoutDistributionReportModelDetails'
import PayoutReportSummary from 'src/clinic/views/clinicBrandPayoutReports/components/PayoutReportSummary'
import { BrandPayoutStyledSmartTable } from 'src/clinic/views/clinicBrandPayoutReports/styledComponents'
import { buildTherapistPayoutReportsExcel } from 'src/clinic/views/clinicBrandPayoutReports/util'
import ExportPayoutReportsDialogWithButton from 'src/common/components/payoutReports/ExportPayoutReportsDialogWithButton'
import { GenericHeaderBackButton } from 'src/consumer/components/ActivityPlansPage/MyPlanComponents'
import { CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import {
  BOOKED_EVENT_MODES,
  EVENT_TYPES_ARRAY,
  MOMENT_FORMATS,
  PAYMENT_METHOD_KV_MAP,
} from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import {
  getCSmartTableColumnData,
  getGenderFromGenderId,
  isValidNumber,
} from 'src/consumer/helpers/utilFunctions'
import { PageMainContentContainer } from 'src/mentor/components/MentorAvailabilityPage/MentorAvailabilityPageStyles'
import {
  getDataMapFromCommonResponse,
  getPriceStringWithCurrency,
  getUniqueClinicBrandsFromMentorAssociatedClinics,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { PaymentConfigMenuWithDialog } from 'src/supportPortal/components/common/paymentConfig/PaymentConfigExample'
import TeacherPayoutReportsFilters from './components/TeacherPayoutReportsFilters'
import {
  TeacherPayoutFilterDialogPaper,
  TeacherPayoutReportsFiltersContainer,
} from './components/styledComponents'
import { checkIfReportNeedsReconsiliationForTherapists } from 'src/mentor/components/TeacherPayoutReports/utils'

export default function TeacherPayoutReports({
  showSummaryReport = false,
  eventType,
  clinicAssociationData,
  showAdminActions = false,
  hideBackButton = false,
  reportListApiSignature = '/api/secure/teacher/payout-reports/paginated',
  summaryReportApiSignature = '/api/secure/teacher/payout-reports/summary',
  reportsListingApiParams,
  summaryReportApiParams,
  useLocalDateFormatForApiCalls = false,
  bookedEventLogApiUrl = '/api/secure/teacher/booked-event-logs/paginated',
  allEventTypes,
}) {
  const clinicBrands = getUniqueClinicBrandsFromMentorAssociatedClinics(clinicAssociationData)

  // Ensure initial dates are always valid
  // Use local time for initial dates to avoid timezone conversion issues
  const getInitialStartDate = () => {
    const date = dayjs().startOf('day')
    if (date.isValid()) {
      return date
    }
    // Ultimate fallback
    return dayjs(new Date()).startOf('day')
  }
  
  const getInitialEndDate = () => {
    const date = dayjs().endOf('day')
    if (date.isValid()) {
      return date
    }
    // Ultimate fallback
    return dayjs(new Date()).endOf('day')
  }

  const [reportDate, setReportDate] = useState(getInitialStartDate())
  const [reportEndDate, setReportEndDate] = useState(getInitialEndDate())
  const [listData, setListData] = useState([])
  const [paginationData, setPaginationData] = useState({ page: 0, totalPages: 0 })
  const [loadingPageData, setLoadingPageData] = useState(false)
  const [summaryReport, setSummaryReport] = useState()
  const [loadingSummaryReport, setLoadingSummaryReport] = useState(false)
  const [chooseDateCalendarVisible, setChooseDateCalendarVisible] = useState(false)
  const [filters, setFilters] = useState({ eventType: 'therapy-session' })
  const [showFilterDialog, setShowFilterDialog] = useState(false)
  const [excelDialogOpen, setExcelDialogOpen] = useState(false)
  const [showBookedEventLogsDialog, setShowBookedEventLogsDialog] = useState(false)
  const [selectedBookedEvent, setSelectedBookedEvent] = useState(null)
  const isAnyFilterApplied =
    !!clinicBrands?.find((cbr) => cbr?.id === filters?.['clinic-brand-id']) ||
    !!filters?.['patient-number'] ||
    !!filters?.nonClinicalEvents

  async function getReports({
    page = 0,
    reportDateMoment = safeDayjsTz(),
    reportEndDateMoment = safeDayjsTz(),
    filterQuery,
  }) {
    const resp = await getCPDataWithParams(reportListApiSignature, {
      page,
      eventType,
      startTime: reportDateMoment
        
        .format(
          useLocalDateFormatForApiCalls
            ? MOMENT_FORMATS.javaLocalDateFormatNew
            : MOMENT_FORMATS.javaInstantFormatNew,
        ),
      endTime: reportEndDateMoment
        
        .format(
          useLocalDateFormatForApiCalls
            ? MOMENT_FORMATS.javaLocalDateFormatNew
            : MOMENT_FORMATS.javaInstantFormatNew,
        ),
      ...(filterQuery || {}),
      ...(reportsListingApiParams || {}),
    })
    if (resp.status === 200) {
      const data = resp.data.content
      const paginationData = resp?.data?.pageable

      const pageNumber = paginationData?.pageNumber
      const totalPages = resp?.data?.totalPages

      setListData(data)
      setPaginationData((prev) => ({ ...prev, page: pageNumber, totalPages: totalPages }))
    }
  }

  async function getSummaryReports({ reportDateMoment, reportEndDateMoment, filterQuery }) {
    setLoadingSummaryReport(true)
    let stTime = reportDateMoment
      
      .startOf('day')
      .utc()
      .format(MOMENT_FORMATS.javaInstantFormatNew)
    let eTime = reportEndDateMoment
      
      .endOf('day')
      .utc()
      .format(MOMENT_FORMATS.javaInstantFormatNew)
    // if (reportDateMoment.isSame(reportEndDateMoment, 'day')) {
    //   eTime = reportDateMoment
    //     
    //     .endOf('day')
    //     .utc()
    //     .format(MOMENT_FORMATS.javaInstantFormatNew)
    // }
    const resp = await getCPDataWithParams(summaryReportApiSignature, {
      eventType,
      startTime: stTime,
      endTime: eTime,
      ...(filterQuery || {}),
      ...(summaryReportApiParams || {}),
    })
    if (resp.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp)
        const reportData = data?.['payout-distribution-summary-for-mentor']
        setSummaryReport(reportData)
      }
    }
    setLoadingSummaryReport(false)
  }

  async function setup() {
    getDataWithSortPageOrSize(0, undefined, undefined, undefined, undefined, undefined, true)
  }

  function getAndSetAppropriatePaginationData(page, sort, pageSize) {
    let pg = page
    let srt = sort
    let pgSize = pageSize
    if (!isValidNumber(pg)) {
      pg = paginationData.page
    }

    setPaginationData((prev) => ({
      ...prev,
      page: pg,
      pageSize: pgSize,
    }))

    return { page: pg, sort: srt, pageSize: pgSize }
  }

  function getAndSetAppropriateDate(newMoment) {
    let date = newMoment
    if (!date || !dayjs.isDayjs(date) || !date.isValid()) {
      if (reportDate && dayjs.isDayjs(reportDate) && reportDate.isValid()) {
        date = reportDate
      } else {
        try {
          date = safeDayjsTz().startOf('day')
        } catch {
          date = dayjs().startOf('day')
        }
      }
    }
    // Ensure the date is valid before setting
    if (!dayjs.isDayjs(date) || !date.isValid()) {
      try {
        date = safeDayjsTz().startOf('day')
      } catch {
        date = dayjs().startOf('day')
      }
    }
    setReportDate(date)
    return date
  }

  function getAndSetAppropriateEndDate(newMoment) {
    let date

    // If newMoment is provided and is a valid moment object, use it directly
    if (newMoment && dayjs.isDayjs(newMoment) && newMoment.isValid()) {
      date = newMoment
    }
    // If newMoment is not a moment object but could be converted to a valid date
    else if (newMoment && !dayjs.isDayjs(newMoment)) {
      const tempMoment = dayjs(newMoment)
      if (tempMoment.isValid()) {
        date = tempMoment
      }
    }

    // If we still don't have a valid date, fall back to existing reportEndDate or current moment
    if (!date) {
      if (reportEndDate && dayjs.isDayjs(reportEndDate) && reportEndDate.isValid()) {
        date = reportEndDate
      } else {
        // Ultimate fallback to current moment with timezone
        try {
          date = safeDayjsTz().endOf('day')
        } catch {
          date = dayjs().endOf('day')
        }
      }
    }

    setReportEndDate(date)
    return date
  }

  function getAppropriateFilters(apiFilters) {
    let filtrs = apiFilters

    if (!filtrs) {
      filtrs = filters
    }

    return filters
  }

  async function getDataWithSortPageOrSize(
    page,
    sort,
    pageSize,
    newSelectedMoment,
    apiFilters,
    newSelectedEndMoment,
    getSummary = false,
  ) {
    setLoadingPageData(true)

    const paginationData = getAndSetAppropriatePaginationData(page, sort, pageSize)
    const newMoment = getAndSetAppropriateDate(newSelectedMoment)
    const newEndMoment = getAndSetAppropriateEndDate(newSelectedEndMoment)
    const fltrs = getAppropriateFilters(apiFilters)
    let promises = [
      getReports({
        page: paginationData?.page,
        reportDateMoment: newMoment,
        reportEndDateMoment: newEndMoment,
        filterQuery: fltrs,
      }),
    ]
    if (showSummaryReport && getSummary) {
      promises = [
        ...promises,
        getSummaryReports({
          reportDateMoment: newMoment,
          reportEndDateMoment: newEndMoment,
          filterQuery: fltrs,
        }),
      ]
    }
    await Promise.all(promises)

    setLoadingPageData(false)
  }

  function closeFilterDialog() {
    setShowFilterDialog(false)
  }
  function openFilterDialog() {
    setShowFilterDialog(true)
  }
  function resetFilters() {
    setFilters({})
  }

  function openBookedEventLogsDialog(bookedEvent) {
    setSelectedBookedEvent(bookedEvent)
    setShowBookedEventLogsDialog(true)
  }

  function closeBookedEventLogsDialog() {
    setShowBookedEventLogsDialog(false)
    setSelectedBookedEvent(null)
  }

  // Function to refresh reports while preserving current date range
  function refreshReportsWithCurrentDateRange() {
    getDataWithSortPageOrSize(0, undefined, undefined, reportDate, undefined, reportEndDate, true)
  }

  useEffect(() => {
    getDataWithSortPageOrSize(0, null, null, null, filters, null, true)
  }, [filters])

  useEffect(() => {
    setup()
  }, [])

  console.log({ summaryReport, reportDate })

  return (
    <>
      {!hideBackButton && (
        <GenericHeaderBackButton
          clickfunction={() => window.history.back()}
          styles={{ margin: '20px' }}
        />
      )}
      <PageMainContentContainer style={{ maxWidth: '1200px' }}>
        <EDVCParent>
          <div className="d-flex flex-column align-items-center gap-1 w-100">
            <FloatingHeaderContainerDayCal style={{ zIndex: 1 }} className="w-100">
              <div
                className="w-100 d-flex flex-column"
                style={{ position: 'relative', padding: '8px' }}
              >
                <div className="d-flex align-items-center justify-content-between w-100 mb-2">
                  <EDVCDateContainer style={{ padding: '8px 0', flex: 1 }}>
                    <div className="d-flex w-100 justify-content-center">
                      <DateRangePicker
                        clearIcon={null}
                        onChange={(val) => {
                          // Validate date values before passing to dayjs
                          let startDate = null
                          let endDate = null
                          
                          if (val?.[0]) {
                            const parsedStart = dayjs(val[0])
                            if (parsedStart.isValid()) {
                              startDate = parsedStart.startOf('day')
                            }
                          }
                          
                          if (val?.[1]) {
                            const parsedEnd = dayjs(val[1])
                            if (parsedEnd.isValid()) {
                              endDate = parsedEnd.startOf('day')
                            }
                          }

                          // Only proceed if we have valid dates
                          if (startDate && endDate) {
                            getDataWithSortPageOrSize(
                              undefined,
                              undefined,
                              undefined,
                              startDate,
                              undefined,
                              endDate,
                              true,
                            )
                          }
                        }}
                        value={[
                          safeToDate(reportDate),
                          safeToDate(reportEndDate),
                        ]}
                      />
                    </div>
                  </EDVCDateContainer>
                  <div className="d-flex align-items-center gap-1">
                    <ExportPayoutReportsDialogWithButton
                      dataFetchingApiUrl={reportListApiSignature}
                      startDate={reportDate}
                      endDate={reportEndDate}
                      entityId={reportsListingApiParams?.['teacher-id']}
                      entityType={'mentor'}
                      eventType={filters?.eventType}
                      allEventTypes={allEventTypes}
                      fileNamePrefix={'Practitioner payout reports'}
                      extraParams={{
                        ...reportsListingApiParams,
                        ...filters,
                        eventType,
                      }}
                      payoutReportDataArrayToExcelFormatArrayBuilder={
                        buildTherapistPayoutReportsExcel
                      }
                      useLocalDateFormatForApiCalls={useLocalDateFormatForApiCalls}
                    />
                    {showAdminActions && (
                      <>
                        <PaymentConfigMenuWithDialog
                          payoutEntityId={reportsListingApiParams?.['teacher-id']}
                          payoutEntityType={'mentor'}
                          entityName={'Practitioner'}
                        />
                      </>
                    )}
                  </div>
                </div>
                {!chooseDateCalendarVisible && (
                  <PayoutReportSummary
                    summaryReport={summaryReport}
                    eventType={filters?.eventType}
                    allEventTypes={allEventTypes}
                    forMentor
                    currentDate={reportEndDate}
                    previousDate={reportDate}
                    // endDate={reportEndDate}
                    loadingSummary={loadingSummaryReport}
                    totalSessionTitle={`Total ${
                      EVENT_TYPES_ARRAY.find((eve) => eve?.id === eventType)?.title || ''
                    } Sessions`}
                    // filters={filters}
                    forTherapist
                    // entityName={selectedMentor?.title || selectedMentor?.name || selectedMentor?.firstName}
                  />
                )}
                <TeacherPayoutReportsFiltersContainer>
                  {/* Left side: Filters and Event Type */}
                  <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center gap-1">
                      <Button
                        variant="contained"
                        sx={{
                          borderRadius: '500px',
                          minWidth: '0px',
                          padding: '6px',
                          marginRight: '6px',
                        }}
                        onClick={() => openFilterDialog()}
                      >
                        <FilterAlt sx={{ fontSize: '16px' }} />
                      </Button>
                      <strong style={{ cursor: 'pointer' }} onClick={() => openFilterDialog()}>
                        {isAnyFilterApplied ? 'Applied ' : ''}Filters
                        {isAnyFilterApplied ? (
                          <>
                            {' '}
                            (
                            <span
                              style={{
                                color: 'blue',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                              onClick={(e) => {
                                e.stopPropagation()
                                resetFilters()
                              }}
                            >
                              reset
                            </span>
                            )
                          </>
                        ) : (
                          <></>
                        )}{' '}
                        {isAnyFilterApplied ? ':' : ''}{' '}
                      </strong>
                    </div>
                    <div
                      style={{
                        width: '300px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <label className="flex-shrink-0">Event Type: </label>
                      <CustomSelect
                        options={EVENT_TYPES_ARRAY?.filter(
                          (eve) => eve?.id === 'therapy-session' || eve?.id === 'screening',
                        )}
                        value={filters?.eventType}
                        getOptionLabel={(option) => option?.title}
                        getOptionValue={(option) => option?.id}
                        selectType={selectTypes.string}
                        onChangeEvent={(e) => {
                          setFilters((prev) => ({ ...prev, eventType: e.target.value }))
                        }}
                      />
                    </div>
                  </div>

                  {/* Right side: Pagination */}
                  <div className="d-flex align-items-center flex-shrink-0">
                    {paginationData?.totalPages > 1 && (
                      <Pagination
                        count={paginationData?.totalPages}
                        page={paginationData?.page + 1}
                        variant="outlined"
                        shape="rounded"
                        onChange={(event, page) =>
                          getDataWithSortPageOrSize(
                            page - 1,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            false,
                          )
                        }
                        color="primary"
                      />
                    )}
                  </div>
                </TeacherPayoutReportsFiltersContainer>

                {/* Applied filters display */}
                <div className="d-flex align-items-center gap-3 mb-2">
                  {clinicBrands?.find((cbr) => cbr?.id === filters?.['clinic-brand-id']) && (
                    <Chip
                      label={`Clinic: ${
                        clinicBrands?.find((cbr) => cbr?.id === filters?.['clinic-brand-id'])
                          ?.title || ''
                      }`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {!!filters?.['patient-number'] && (
                    <Chip
                      label={`Patient: ${filters?.['patient-number']}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {!!filters?.nonClinicalEvents && (
                    <Chip
                      label={`Non Clinical: ${
                        filters?.nonClinicalEvents === 'true' ? 'Yes' : 'No'
                      }`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {!!filters?.markedCompletedEvents && (
                    <Chip
                      label={`${
                        filters?.markedCompletedEvents === 'true'
                          ? 'Marked Completed Bookings'
                          : 'Not Marked Completed Bookings'
                      }`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </div>
              </div>
            </FloatingHeaderContainerDayCal>
            <BrandPayoutStyledSmartTable>
              <CSmartTable
                items={listData?.map((itm) => {
                  const needsReconsiliation = checkIfReportNeedsReconsiliationForTherapists(itm)
                  return {
                    ...itm,
                    _props: {
                      color: needsReconsiliation ? 'danger' : undefined,
                    },
                  }
                })}
                columns={columns({})}
                loading={loadingPageData}
                tableProps={{
                  hover: true,
                  striped: true,
                  responsive: true,
                }}
                // columnSorter
                // pagination
                // onColumnFilterChange={(filter) => {
                //   getDataWithSortPageOrSize(1)
                //   setColumnFilter(filter)
                // }}
                // onItemsPerPageChange={(pageSize) => {
                //   getDataWithSortPageOrSize(1, undefined, pageSize)
                //   setItemsPerPage(pageSize)
                // }}
                itemsPerPage={10000000000000000}
                scopedColumns={scopedColumns({
                  showAdminActions,
                  refreshReports: refreshReportsWithCurrentDateRange,
                  openBookedEventLogsDialog,
                })}
              />
            </BrandPayoutStyledSmartTable>
          </div>
        </EDVCParent>
        <Dialog
          open={showFilterDialog}
          PaperComponent={TeacherPayoutFilterDialogPaper}
          onClose={closeFilterDialog}
        >
          <TeacherPayoutReportsFilters
            finalFilters={filters}
            setFinalFilters={setFilters}
            clinicBrands={clinicBrands}
            closeDialog={closeFilterDialog}
          />
        </Dialog>
        {/* <Dialog
          open={excelDialogOpen}
          PaperComponent={PayoutReportExportPaper}
          onClose={() => setExcelDialogOpen(false)}
        >
          <ExportPayoutReports
            closeDialog={() => setExcelDialogOpen(false)}
            dataFetchingApiUrl={reportListApiSignature}
            startDate={reportDate}
            endDate={reportEndDate}
            entityId={reportsListingApiParams?.['teacher-id']}
            entityType={'mentor'}
            fileNamePrefix={
              eventType === 'consultation'
                ? 'Therapist_Consultation_Payout_Reports'
                : 'Therapist_Therapy_Payout_Reports'
            }
            extraParams={{
              ...reportsListingApiParams,
              ...filters,
              eventType,
            }}
            payoutReportDataArrayToExcelFormatArrayBuilder={buildTherapistPayoutReportsExcel}
            useLocalDateFormatForApiCalls={useLocalDateFormatForApiCalls}
          />
        </Dialog> */}
        {selectedBookedEvent && (
          <BookedEventLogsDrawer
            open={showBookedEventLogsDialog}
            onClose={closeBookedEventLogsDialog}
            bookedSlot={{
              ...selectedBookedEvent,
              id: selectedBookedEvent?.bookedEventId,
              startTime: selectedBookedEvent?.bookedEventStartTime,
              endTime: selectedBookedEvent?.bookedEventEndTime,
              patientInfo: {
                name: selectedBookedEvent?.patientName,
                patientNumber: selectedBookedEvent?.patientNumber,
                gender: selectedBookedEvent?.patientGender,
                dateOfBirth: selectedBookedEvent?.patientDateOfBirth,
                mobile: selectedBookedEvent?.patientMobile,
              },
            }}
            apiEndpoint={bookedEventLogApiUrl}
          />
        )}
      </PageMainContentContainer>
    </>
  )
}

const columns = ({ isForBrand }) => {
  return [
    getCSmartTableColumnData({
      key: 'patientName',
      label: 'Patient Name',
      filter: false,
      sorter: false,
      _style: { minWidth: '250px' },
    }),
    getCSmartTableColumnData({
      key: 'eventDetails',
      label: 'Booking Details',
      filter: false,
      sorter: false,
      _style: { minWidth: '250px' },
    }),
    getCSmartTableColumnData({
      key: 'feeDetails',
      label: 'Fee Details',
      filter: false,
      sorter: false,
      _style: { minWidth: '150px' },
    }),
    getCSmartTableColumnData({
      key: 'settlementDetails',
      label: 'Mentor Share',
      filter: false,
      sorter: false,
      _style: { minWidth: '150px' },
    }),
    getCSmartTableColumnData({
      key: 'view',
      label: 'View',
      filter: false,
      sorter: false,
      _style: { minWidth: '150px' },
    }),
  ]
}
const scopedColumns = ({
  isForBrand,
  propEventType,
  showAdminActions,
  refreshReports,
  openBookedEventLogsDialog,
}) => {
  return {
    collectedBy: (item) => (
      <td>
        <div className="d-flex flex-column">
          <span>
            (
            {item?.paymentMethod ===
            PAYMENT_METHOD_KV_MAP.BOOKING_FEE_PAYMENT_METHOD_COLLECTED_BY_AUMHUM
              ? 'Collected By AUMHUM'
              : 'Collected By Clinic'}
            )
          </span>
          <span>{item?.collectedBy}</span>
          <span>
            {item?.collectedAt
              ? dayjs
                  .utc(item?.collectedAt)
                  .local()
                  .format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)
              : 'No Data'}
          </span>
        </div>
      </td>
    ),
    clinicBrandDetails: (item) => {
      return (
        <td>
          <PayoutDistributionReportModelDetails
            report={item}
            getSharePercent={(item) => item?.sourceSharePercentage}
            getShareAmount={(item) => item?.sourceShare}
            getEntityTitle={(item) => item?.sourceName}
            getEntityFeeModelDetails={(item) => item?.sourceEntityFeeModelUsed}
            getFeeModelDetails={(item) => item?.sourceFeeModelUsed}
            getCurrencyCode={(item) => item?.currency}
          />
        </td>
      )
    },
    grossMargin: (item) => (
      <td>
        {isValidNumber(item?.grossMargin)
          ? getPriceStringWithCurrency(item?.grossMargin, item?.currency)
          : 'Cannot Calculate'}
      </td>
    ),
    visitDetails: (item) => {
      return (
        <td>
          <div className="d-flex flex-column gap-1">
            <span>Visit Number: {item?.sessionNumOfSameTypeWithSameMentor}</span>
            <span>
              Fee Paid:{' '}
              {isValidNumber(item?.feeCollected)
                ? getPriceStringWithCurrency(item?.feeCollected, item?.currency)
                : 'No Data'}
            </span>
          </div>
        </td>
      )
    },
    eventDetails: (item) => {
      // const foundEventType = EVENT_TYPES_ARRAY.find((type) => item?.eventType === type?.id)
      return (
        <td>
          <div className="d-flex flex-column gap-1">
            {item?.clinicBrandId ? (
              <div>
                Clinic: {item?.sourceName} {item?.clinicLocationName}
              </div>
            ) : (
              'Non Clinical Event'
            )}
            <div>
              <span>
                Scheduled Start Time<strong>: </strong>
                {item?.bookedEventStartTime
                  ? dayjs
                      .utc(item?.bookedEventStartTime)
                      .local()
                      .format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)
                  : 'No Data'}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div>
                {BOOKED_EVENT_MODES?.[item?.mode]?.altTitle ? (
                  <Chip label={BOOKED_EVENT_MODES?.[item?.mode]?.altTitle} />
                ) : (
                  ''
                )}
              </div>
              {!!item?.markedCompleted ? (
                <Chip label={'Completed'} color="success" />
              ) : (
                <Chip label={'Not Completed'} />
              )}
              {item?.testPatient ? <Chip color="warning" label="Test Booking" /> : ''}
              {item?.reconciliationApplied && (
                <Chip
                  color="warning"
                  label="Reconciled"
                  size="small"
                  title={
                    item?.reconciliationAdjustment?.note ||
                    'This booking has been reconciled'
                  }
                />
              )}
            </div>
            <div>
              <div className="d-flex align-items-center gap-2">
                {item?.nextVisitDate && <Chip color="success" label={`Next Visit Date Given`} />}
                {isValidNumber(item?.reportContentLength) && (
                  <Chip
                    color={item?.reportContentLength < 50 ? 'warning' : 'default'}
                    label={`Report Content Length: ${item?.reportContentLength} chars`}
                  />
                )}
              </div>
            </div>
            <div>Visit #: {item?.sessionNumOfSameTypeWithSameMentor}</div>
            {/* <div className="flex-shrink-0">
                {item?.markedCompleted ? (
                  <Chip color="success" size="small" label="Session completed" />
                ) : (
                  <Chip color="default" size="small" label="Session not completed" />
                )}
              </div> */}
          </div>
        </td>
      )
    },
    mentorDetails: (item) => {
      return (
        <td>
          <PayoutDistributionReportModelDetails
            report={item}
            getEntityTitle={(item) => item?.mentorName}
            getSharePercent={(item) => item?.mentorSharePercentage}
            getShareAmount={(item) => item?.mentorShare}
            getEntityFeeModelDetails={(item) => item?.mentorEntityFeeModelUsed}
            getFeeModelDetails={(item) => item?.mentorFeeModelUsed}
            getCurrencyCode={(item) => item?.currency}
          />
        </td>
      )
    },
    feeDetails: (item) => {
      return (
        <td>
          <PayoutDistributionReportFeeDetails
            report={item}
            getClinicShare={(item) => item?.mentorShare}
            hideClinicShare
            hideAumhumShare
            hideFeeDataOnZeroPercentage
            getClinicSharePercentage={(item) => item?.mentorSharePercentage}
            getCurrency={(item) => item?.currency}
            forMentor
            getMinPayableValue={(item) => item?.minFeeUsedForCalculations}
          />
        </td>
      )
    },
    startTime: (item) => {
      return (
        <td>
          {item?.bookedEventStartTime
            ? dayjs
                .utc(item?.bookedEventStartTime)
                .local()
                .format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)
            : 'No Data'}
        </td>
      )
    },
    patientName: (item) => {
      return (
        <td>
          <div className="d-flex flex-column gap-2">
            <span>{item?.patientNumber}</span>
            <span>{item?.patientName}</span>
            <span>
              {getGenderFromGenderId(item?.patientGender)?.label || 'NA'}/
              {getAgeFromDobWithTime(item?.patientDateOfBirth)}
            </span>
          </div>
        </td>
      )
    },
    settlementDetails: (item) => {
      let shareIsZero =
        isValidNumber(item?.mentorSharePercentage) && item?.mentorSharePercentage === 0
      if (shareIsZero) {
        return <td>NA</td>
      }

      return (
        <td>
          <div>
            {isValidNumber(item?.mentorShare)
              ? getPriceStringWithCurrency(item?.mentorShare, item?.currency)
              : 'No Data'}{' '}
          </div>
          <div>
            (
            {isValidNumber(item?.mentorSharePercentage)
              ? `${item?.mentorSharePercentage}%${
                  isValidNumber(item?.minFeeUsedForCalculations)
                    ? ` of ${getPriceStringWithCurrency(
                        item?.minFeeUsedForCalculations,
                        item?.currency,
                      )} (min agreed)`
                    : ''
                }`
              : 'No Data'}
            )
          </div>
        </td>
      )
    },
    view: (item) => {
      return (
        <td>
          <div className="d-flex flex-column gap-2">
            <ClinicBrandPayoutReportsViewDetails
              hideFeeDetailsWhenShareZero
              forMentor
              report={item}
              showAdminActions={showAdminActions}
              refreshReports={refreshReports}
              showBookingLogsButton
              openBookedEventLogsDialog={() => openBookedEventLogsDialog(item)}
            />
          </div>
        </td>
      )
    },
  }
}
